import { useRegisterSW } from "virtual:pwa-register/react";
import { useEffect, useRef, useState } from "react";
import { UPDATE_SEEN_KEY, updateAction } from "./pwa-update.ts";
import { useSyncStatus } from "./sync-status.ts";

function readSeen(): boolean {
  try {
    return localStorage.getItem(UPDATE_SEEN_KEY) === "1";
  } catch {
    return false;
  }
}

function writeSeen(seen: boolean): void {
  try {
    if (seen) localStorage.setItem(UPDATE_SEEN_KEY, "1");
    else localStorage.removeItem(UPDATE_SEEN_KEY);
  } catch {
    // Private mode or storage blocked: the update then applies on the next real restart.
  }
}

export interface AppUpdate {
  /** A new version is installed and will apply on the next launch. */
  ready: boolean;
  /** "Restart now" is allowed: nothing is waiting to sync. */
  canRestart: boolean;
  restart: () => void;
}

/**
 * Registers the service worker (prompt mode) and applies updates on the next
 * launch. The waiting worker otherwise takes over by itself once every tab
 * of the old version has closed, which is also "next launch".
 */
export function useAppUpdate(): AppUpdate {
  const seenAtLaunch = useRef(readSeen());
  const pending = useSyncStatus((s) => s.pending + s.rejected);
  const {
    needRefresh: [waiting],
    updateServiceWorker,
  } = useRegisterSW({
    // Check for a new version hourly while the app stays open (installed PWAs rarely restart).
    onRegisteredSW(_url, registration) {
      if (!registration) return;
      setInterval(() => void registration.update().catch(() => undefined), 60 * 60 * 1000);
    },
  });
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!waiting) {
      writeSeen(false);
      return;
    }
    const action = updateAction({
      waiting,
      seenInEarlierSession: seenAtLaunch.current,
      unsynced: pending,
    });
    if (action === "apply-now" && !applying) {
      setApplying(true);
      writeSeen(false);
      void updateServiceWorker(true);
      return;
    }
    writeSeen(true);
  }, [waiting, pending, applying, updateServiceWorker]);

  return {
    ready: waiting && !applying,
    canRestart: pending === 0,
    restart: () => {
      if (pending > 0) return;
      writeSeen(false);
      void updateServiceWorker(true);
    },
  };
}
